import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Reveal } from "@/components/Reveal";
import { api, BOOKINGS_OPEN, CONTACT_EMAIL } from "@/lib/api";
import { pick, lang2 } from "@/lib/localize";

export default function SearchResults() {
  const { t, i18n } = useTranslation();
  const lang = lang2(i18n.language);
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const checkin = params.get("checkin");
  const checkout = params.get("checkout");
  const guests = parseInt(params.get("guests") || "2");

  useEffect(() => {
    if (!checkin || !checkout) return setError(true);
    setData(null);
    api
      .get("/availability/search", { params: { checkin, checkout, guests } })
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [checkin, checkout, guests]);

  const money = (v) => new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR" }).format(v);
  const query = `checkin=${checkin}&checkout=${checkout}&guests=${guests}`;

  const available = data ? data.results.filter((r) => r.available) : [];
  const unavailable = data ? data.results.filter((r) => !r.available) : [];

  return (
    <div data-testid="search-results-page" className="bg-sand min-h-screen">
      <div className="noise-overlay" />
      <Navbar />

      <div className="pt-32 pb-24 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-line pb-8 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-terracotta mb-3">{t("search.results")}</p>
            <h1 className="font-serif text-4xl md:text-6xl text-ink leading-tight">
              {data ? t("search.resultsFor", { from: data.checkin, to: data.checkout }) : "…"}
            </h1>
            {data && (
              <p className="mt-3 text-sm text-ink/55">{t("search.stay", { nights: data.nights, guests: data.guests })}</p>
            )}
          </div>
          <Link data-testid="modify-search-link" to="/" className="text-xs uppercase tracking-[0.2em] text-ink/60 hover:text-terracotta transition-colors border-b border-line pb-1">
            {t("search.modify")}
          </Link>
        </div>

        {!data && !error && <p className="text-xs uppercase tracking-[0.3em] text-ink/40">…</p>}
        {error && <p className="text-ink/60">{t("search.noneSub")}</p>}

        {data && available.length === 0 && (
          <div data-testid="no-results" className="border border-line bg-white p-10 text-center">
            <h2 className="font-serif text-3xl text-ink">{t("search.noneTitle")}</h2>
            <p className="mt-3 text-sm text-ink/60 max-w-md mx-auto">
              {!BOOKINGS_OPEN ? t("booking.closed", { email: CONTACT_EMAIL }) : (data.stay_error || t("search.noneSub"))}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-8">
          {available.map(({ apartment: apt, nights, total }, i) => (
            <Reveal key={apt.slug} delay={0.05 * i}>
              <Link
                to={`/apartamento/${apt.slug}?${query}`}
                data-testid={`result-${apt.slug}`}
                className="group grid grid-cols-12 bg-white border border-line overflow-hidden hover:shadow-xl transition-shadow duration-500"
              >
                <div className="col-span-12 md:col-span-4 aspect-[16/10] md:aspect-auto md:min-h-[220px] overflow-hidden">
                  <img src={apt.photos[0]} alt={apt.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                </div>
                <div className="col-span-12 md:col-span-8 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div>
                    <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#4A6B53]">
                      <CheckCircle size={13} weight="fill" /> {t("search.available", { defaultValue: t("booking.legendFree") })}
                    </p>
                    <h2 className="font-serif text-3xl md:text-4xl text-ink mt-2 group-hover:text-terracotta transition-colors">{apt.name}</h2>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-ink/50 mt-2">{apt.zone}</p>
                    <p className="text-sm text-ink/60 mt-3 font-light max-w-md line-clamp-2">{pick(apt.tagline_i18n, lang)}</p>
                  </div>
                  <div className="text-left md:text-right shrink-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-ink/45">{t("search.totalStay")} · {nights} {t("booking.nights")}</p>
                    <p className="font-serif text-3xl md:text-4xl text-ink mt-1">{total != null ? money(total) : "—"}</p>
                    <p className="text-xs text-ink/50">{money(apt.price_per_night)} {t("detail.perNight")}</p>
                    <span className="inline-flex items-center gap-2 mt-4 px-6 py-3 bg-terracotta text-sand text-[10px] uppercase tracking-[0.2em] group-hover:bg-atlantic transition-colors">
                      {t("search.book")} <ArrowUpRight size={13} />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {unavailable.length > 0 && (
          <div className="mt-16">
            <h2 className="text-xs uppercase tracking-[0.3em] text-ink/45 mb-6">{t("search.unavailableTitle")}</h2>
            <div className="flex flex-col gap-3">
              {unavailable.map(({ apartment: apt, reason, alt_dates }) => (
                <div key={apt.slug} data-testid={`result-off-${apt.slug}`} className="bg-white/60 border border-line px-6 py-4 opacity-80">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: apt.color }} />
                      <span className="font-serif text-xl text-ink/70">{apt.name}</span>
                    </div>
                    <span className="flex items-center gap-2 text-xs text-ink/50">
                      <XCircle size={14} className="text-terracotta/70" />
                      {reason === "capacity" ? t("search.noCapacity", { max: apt.guests }) : t("search.noDates")}
                    </span>
                  </div>
                  {alt_dates && alt_dates.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-[10px] uppercase tracking-[0.15em] text-ink/45">{t("search.altDates")}:</span>
                      {alt_dates.map((d) => (
                        <Link
                          key={d.checkin}
                          data-testid={`alt-date-${apt.slug}-${d.checkin}`}
                          to={`/apartamento/${apt.slug}?checkin=${d.checkin}&checkout=${d.checkout}&guests=${guests}`}
                          className="text-[11px] border border-terracotta/50 text-terracotta px-3 py-1.5 hover:bg-terracotta hover:text-sand transition-colors"
                        >
                          {d.checkin} → {d.checkout}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
